import { useSelector } from 'react-redux';
import { format as formatDate } from 'date-fns';
import { de } from 'date-fns/locale';
import { SubscriptionDetailsResponse } from '@klab-berlin/api-sdk/lib/types/responses/Billing';

import { dateTimeFormat, subscriptionDateFormat } from '../constants';
import { getStripeSubscriptionDetails, getSubscription, selectUser } from '../reducers/user.selectors';
import { currencyFormat } from '../utils/format';
import { isWithinWithdrawalWindow } from './withdrawalEligibility';

export enum SubscriptionStatus {
  Active = 'Active',
  Cancelled = 'Cancelled',
  PauseRequested = 'Pause requested',
  Paused = 'Paused',
  Blocked = 'Blocked',
  WithdrawalRequested = 'WithdrawalRequested',
}

// SM-191: map the synced Stripe subscription status to the shared
// SubscriptionStatus used by the label chip and notification alert. Pause /
// PauseRequested are intentionally omitted — Stripe doesn't expose the pause
// date range yet (see ticket), and pause-requested is a legacy-only flow.
const mapStripeSubscriptionStatus = (
  details: SubscriptionDetailsResponse | undefined,
): SubscriptionStatus => {
  const subscription = details?.subscription;
  if (!subscription) return SubscriptionStatus.Active;

  // An in-flight withdrawal (Widerruf) takes precedence, mirroring the legacy path.
  if (details?.revocation?.request) return SubscriptionStatus.WithdrawalRequested;
  if (subscription.isCanceled || subscription.status === 'canceled' || subscription.cancelAt) {
    return SubscriptionStatus.Cancelled;
  }
  if (subscription.status === 'past_due' || subscription.status === 'unpaid') {
    return SubscriptionStatus.Blocked;
  }
  return SubscriptionStatus.Active;
};

export const useSubscriptionInfoData = () => {
  const user = useSelector(selectUser);
  const subscription = useSelector(getSubscription);
  const stripeSubscriptionDetails = useSelector(getStripeSubscriptionDetails);

  // user type 1 B2C and B2B without subscription
  const isBasicUserWithoutSubscription = !!user?.isBasisUser && !subscription;
  // user type 2,3,4 B2B
  const isBusinessUser = !!user?.isSchoolUser || !!user?.isLibraryUser || !!user?.isKeycloakSSO;
  // user type 1,2,3,4 B2C with subscription
  const isB2CUserWithSubscription = !isBasicUserWithoutSubscription && !isBusinessUser;
  const pauseRequested = user?.pauseExperimentStatus === 'requested';
  const withdrawalRequested = user?.withdrawalRequestStatus === 'requested';
  let withdrawalSubmittedAt = user?.withdrawalRequestedAt
    ? formatDate(new Date(user.withdrawalRequestedAt), dateTimeFormat, { locale: de })
    : undefined;
  const canUpgrade = !!user?.canUpgrade;
  const allowAutomaticCancellation = !!user?.allowAutomaticCancellation;
  const isInTrial = !!subscription?.trial?.isInTrial;
  const withinWithdrawalWindow = isWithinWithdrawalWindow(subscription);

  const lastProduct = subscription?.products
    .filter(product => !product.plan?.name.includes('PromoCode'))
    .at(-1);

  let productName = lastProduct ? `${lastProduct.name}, ${lastProduct.plan.name}`: '';
  let price = currencyFormat(subscription?.licence?.pricePerMonth || 0);
  let reducedPrice = price;
  let discountMonths = 0;

  let rawStartDate = subscription?.startDate;
  let rawEndDate = subscription?.endDate;

  let priceText = 'price per month';
  let discountText = '';
  let pauseStartDate, pauseEndDate;

  let subscriptionStatus = subscription?.status as SubscriptionStatus || SubscriptionStatus.Active;

  if (isB2CUserWithSubscription) {
    const isCancelledSubscription = subscription?.status === SubscriptionStatus.Cancelled;
    const isSuspendedSubscription = subscription?.status === 'Suspended';
    const isScheduledToCancel = subscription?.followingSubscription.isScheduledToCancel;
    const hasPromoCode = !!subscription?.promoCode && !!subscription?.discounts;

    if (isSuspendedSubscription) {
      rawEndDate = subscription?.followingSubscription?.endDate;
    }

    if (isInTrial) {
      rawEndDate = subscription.trial?.endDate;
      rawStartDate = subscription.trial?.startDate;
      priceText = isCancelledSubscription ? '' : 'price after trial';
    }

    if (isScheduledToCancel) {
      rawEndDate = subscription.followingSubscription.endDate;
    }

    if (hasPromoCode) {
      const firstDiscount = subscription.discounts[0];
      if (firstDiscount?.discountPercentage) {
        discountText = `${firstDiscount.discountPercentage}%`;
      } else if (firstDiscount?.discountAmount) {
        discountText = currencyFormat(firstDiscount.discountAmount);
      }
      if (firstDiscount?.isFixedPeriod) {
        discountMonths = firstDiscount.upToPeriods;
        priceText = isInTrial
          ? isCancelledSubscription
            ? ''
            : `${priceText} after discount`
          : 'price after discount';
        reducedPrice = currencyFormat(subscription.reducedPrice);
      } else {
        price = currencyFormat(subscription.reducedPrice);
      }
    }

    if (pauseRequested) {
      subscriptionStatus = SubscriptionStatus.PauseRequested;
    }

    // Withdrawal takes precedence over pause so the chip always reflects the most recent user action.
    if (withdrawalRequested) {
      subscriptionStatus = SubscriptionStatus.WithdrawalRequested;
    }

    // Pause approved but not in effect yet
    if (subscription?.suspension && subscription.suspension.isPause) {
      pauseStartDate =
        formatDate(new Date(subscription.suspension.startDate), subscriptionDateFormat, { locale: de });
      pauseEndDate = formatDate(new Date(subscription.suspension.endDate), subscriptionDateFormat, { locale: de });
    }

    // Pause in effect
    if (isSuspendedSubscription && subscription.suspension?.isPause) {
      productName += ' (pausiert)';
      subscriptionStatus = SubscriptionStatus.Paused;
    }

    // Suspension in effect
    if (isSuspendedSubscription && !subscription.suspension?.isPause) {
      subscriptionStatus = SubscriptionStatus.Blocked;
    }

  }

  // SM-191: Stripe users have no legacy Zuora subscription, so drive the status
  // chip, the info/warning alert and the end date off the synced Stripe details.
  if (user?.isStripeUser) {
    subscriptionStatus = mapStripeSubscriptionStatus(stripeSubscriptionDetails);

    const stripeSubscription = stripeSubscriptionDetails?.subscription;
    rawStartDate = stripeSubscription?.periodStart ?? rawStartDate;
    // For a scheduled term-end cancellation the effective end is `cancelAt`;
    // otherwise fall back to the current period end.
    rawEndDate = stripeSubscription?.cancelAt ?? stripeSubscription?.periodEnd ?? rawEndDate;

    const revocationSubmittedAt = stripeSubscriptionDetails?.revocation?.request?.submittedAt;
    if (revocationSubmittedAt) {
      withdrawalSubmittedAt = formatDate(new Date(revocationSubmittedAt), dateTimeFormat, { locale: de });
    }
  }

  const startDate = formatDate(new Date(rawStartDate || Date.now()), subscriptionDateFormat, { locale: de });
  const endDate = formatDate(new Date(rawEndDate || Date.now()), subscriptionDateFormat, { locale: de });

  return {
    user,
    subscription,
    isBasicUserWithoutSubscription,
    isBusinessUser,
    isB2CUserWithSubscription,
    price,
    discountMonths,
    reducedPrice,
    productName,
    startDate,
    endDate,
    subscriptionStatus,
    canUpgrade,
    allowAutomaticCancellation,
    priceText,
    discountText,
    pauseStartDate,
    pauseEndDate,
    isInTrial,
    withdrawalSubmittedAt,
    isWithinWithdrawalWindow: withinWithdrawalWindow,
  };
};

export default useSubscriptionInfoData;
